import { Form } from "components/Form/Form";
import FormInput from "components/FormInput/FormInput";
import {
  RequestAccessController,
  useRequestDependencies,
  useRequestStore,
} from "entities/Request";
import { useRequestLinesStore } from "entities/TravelLines";
import { transactionDefaultValues } from "modules/CardStatement/constants/transactionDefaultValues";
import {
  createRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Col, ListGroup, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Form as CustomForm, DatePicker } from "shared/ui";
import FormNumericInput from "../../../../components/FormNumericInput/FormNumericInput";
import TransactionCostLineContainer from "../TransactionCostLine/TransactionCostLine.container";
import dayjs from "dayjs";

const access = new RequestAccessController();

const CardStatementTransaction = forwardRef(
  function CardStatementTransaction(props, ref) {
    const {
      // @ts-ignore
      transaction,
      // @ts-ignore
      addCostLineToSaveStack,
      // @ts-ignore
      addCostLineToSubmitStack,
      // @ts-ignore
      requestIsEditable,
      // @ts-ignore
      cardFormat,
    } = props;
    const transactionForm = useForm({
      defaultValues: transactionDefaultValues,
    });

    const request = useRequestStore((state) => state.request);
    const { data: { validations } = {} } = useRequestDependencies(
      "credit_cards_form_dependencies",
      request.id,
      {
        triggerRequest: access.to("SubCosts", "edit").provided(),
      }
    );

    const {
      reset,
      handleSubmit,
      formState: { errors, isDirty },
      setValue,
    } = transactionForm;

    const [costLinesRefs, setCostLinesRefs] = useState([]);
    useEffect(() => {
      setCostLinesRefs((ref) =>
        Array(transaction?.credit_cards_costs_transaction_sub_costs.data.length)
          .fill()
          .map((_, i) => ref[i] || createRef())
      );
    }, [transaction?.credit_cards_costs_transaction_sub_costs.data]);

    useEffect(() => {
      const formValues = Object.fromEntries(
        Object.entries(transactionDefaultValues).map(([field, value]) => {
          let newValue = transaction?.[field] ?? value;
          if (field === "cost_date" && newValue) {
            newValue = dayjs(newValue);
          }
          if (field === "attachments")
            newValue = transaction?.attachments?.data?.map(
              (fileData) => fileData.id
            );
          return [field, newValue];
        })
      );
      reset({ ...transactionDefaultValues, ...formValues });
    }, [transaction]);

    const triggerCostlinesSave = () => {
      costLinesRefs.forEach((ref) => ref.current.costLineSave());
    };
    const triggerCostlinesSubmit = () => {
      costLinesRefs.forEach((ref) => ref.current.costLineSubmit());
    };

    const addRequestLineToStack = useRequestLinesStore(
      (state) => state.addRequestLineToStack
    );
    const handleRequestLineSave = () => {
      addRequestLineToStack(
        new Promise((resolve) => {
          handleSubmit(
            // @ts-ignore
            ({ attachments }) =>
              resolve({
                isValid: true,
                formData: {
                  isTransaction: true,
                  lineType: request.type,
                  id: transaction.id,
                  attachments,
                  isDirty,
                },
              }),
            (errors) => resolve({ isValid: false, errors })
          )();
        })
      );
    };

    useImperativeHandle(ref, () => ({
      save() {
        if (cardFormat === "corporate") {
          handleRequestLineSave();
        }
        triggerCostlinesSave();
      },
      submit() {
        if (cardFormat === "corporate") {
          handleRequestLineSave();
        }
        triggerCostlinesSubmit();
      },
    }));

    const uploadedFiles = useMemo(
      () =>
        transaction.attachments?.data.map((attachment) => ({
          ...attachment,
          isUploaded: true,
        })),
      [transaction.attachments?.data]
    );

    return (
      <>
        <Form form={transactionForm} onSubmit={(data) => console.log(data)}>
          <Row>
            <FormInput
              name="vendor_name"
              readOnly
              label={cardFormat === "us/ca" ? "Vendor name" : "Employee name"}
              displayAs={Col}
            />
            <FormNumericInput
              name="total_amount"
              addon={transaction?.currency_code}
              readOnly
              label={
                cardFormat === "us/ca"
                  ? "Currency and Gross Amount"
                  : "Total Gross Amount"
              }
              displayAs={Col}
              isAllowed={({ floatValue }) =>
                floatValue === undefined ||
                (floatValue <= 999999999999.99 &&
                  floatValue >= -999999999999.99)
              }
              allowNegative={true}
            />
            <DatePicker
              name="cost_date"
              disabled
              displayAs={Col}
              md={3}
              label={cardFormat === "us/ca" ? "Cost date" : "Transaction date"}
            />
            <FormInput
              name="comment"
              as="textarea"
              label="Comment"
              readOnly
              displayAs={Col}
            />
          </Row>
          {cardFormat === "corporate" && (
            <Row>
              <CustomForm.FilesInput
                label="Attachments"
                name="attachments"
                error={errors?.attachments}
                disabled={!requestIsEditable}
                uploadedFiles={uploadedFiles}
                maxSize={validations?.max_allowed_file_size_in_bytes}
                max={
                  validations?.attachments_count_rules.data?.transactions?.max
                }
                min={
                  validations?.attachments_count_rules.data?.transactions?.min
                }
                acceptedExtensions={validations?.attachments_allowed_file_types}
                displayAs={Col}
                setValue={setValue}
              />
            </Row>
          )}
        </Form>

        <ListGroup as="ul" className="mt-5">
          {transaction?.credit_cards_costs_transaction_sub_costs.data?.map(
            (costLineData, idx) => (
              <ListGroup.Item as="li" key={costLineData.id}>
                <TransactionCostLineContainer
                  ref={costLinesRefs[idx]}
                  // @ts-ignore
                  idx={idx}
                  cardFormat={cardFormat}
                  costLineData={costLineData}
                  transactionId={transaction.id}
                  addCostLineToSaveStack={addCostLineToSaveStack}
                  addCostLineToSubmitStack={addCostLineToSubmitStack}
                  requestIsEditable={requestIsEditable}
                  parentCostSplitIdx={
                    costLineData?.splitted_cost_id
                      ? transaction?.credit_cards_costs_transaction_sub_costs.data.findIndex(
                          (subCost) =>
                            subCost.id === +costLineData.splitted_cost_id
                        )
                      : null
                  }
                />
              </ListGroup.Item>
            )
          )}
        </ListGroup>
      </>
    );
  }
);

export default CardStatementTransaction;
